import type { Transform } from './state';

export interface CropRect {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
}

interface Sized {
  width: number;
  height: number;
}

/**
 * Source-rect for drawing `img` into a cw×ch canvas with "cover" semantics,
 * zoomed by `transform.scale` and panned by `transform.offset*` (in source
 * pixels). The rect is clamped inside the image so panning never reveals
 * empty canvas.
 */
export function getCropRect(img: Sized, cw: number, ch: number, transform: Transform): CropRect {
  const ir = img.width / img.height;
  const cr = cw / ch;
  let baseW: number;
  let baseH: number;
  if (ir > cr) {
    baseH = img.height;
    baseW = baseH * cr;
  } else {
    baseW = img.width;
    baseH = baseW / cr;
  }
  const scale = transform.scale || 1;
  const w = Math.min(img.width, baseW / scale);
  const h = Math.min(img.height, baseH / scale);
  const cx = img.width / 2 + (transform.offsetX || 0);
  const cy = img.height / 2 + (transform.offsetY || 0);
  let sx = cx - w / 2;
  let sy = cy - h / 2;
  sx = Math.max(0, Math.min(img.width - w, sx));
  sy = Math.max(0, Math.min(img.height - h, sy));
  return { sx, sy, sw: w, sh: h };
}

/** Half-width, in canvas px, of the zone around the divider that grabs the handle. */
export function handleGrabPx(canvasWidth: number): number {
  return Math.max(28, canvasWidth * 0.045);
}

export function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
